/**
 * LSP-17745 — on-screen readout of the mobile tab strip's scroll geometry.
 *
 * Gated behind the component's existing `?debug=true` flag (EntityDetailPage's `isDebugMode`, the
 * same switch its upload-status alerts use), so it costs nothing unless someone asks for it.
 *
 * Why an overlay and not just a log: the dead-space state this exists to diagnose only occurs in
 * real iOS Safari — Chromium and desktop WebKit both re-clamp an inner scroller on their own, so
 * neither reproduces it headlessly, and a phone has no console to read. `peak` is retained rather
 * than only the live value because the interesting moment (a zoom) is over before anyone can look.
 *
 * Caller owns the lifecycle: the returned function removes the node and stops the frame loop.
 */

import { isLoadedMeFeatureFlagEnabled } from "@/helpers/meFeatureFlags";

const OVERLAY_ID = "lsp17745-tab-debug";

export function mountTabStripDebugOverlay(): () => void {
	const overlay = document.createElement("div");
	overlay.id = OVERLAY_ID;
	// LSP-18592: only with capture evidence on does the overlay move to the top, above Vuetify's dialog
	// layer (v-dialog is 2400), and show only while a dialog is open — so it never sits over the Capture
	// evidence panel in the lightbox's metadata column. Flag off keeps develop's bottom strip.
	const captureEvidenceOn = isLoadedMeFeatureFlagEnabled("capture_evidence");
	overlay.style.cssText = [
		"position:fixed",
		"left:0",
		...(captureEvidenceOn ? ["top:0", "width:fit-content", "z-index:2500"] : ["right:0", "bottom:0", "z-index:99999"]),
		"background:rgba(0,0,0,.86)",
		"color:#0f0",
		"font:11px/1.35 ui-monospace,Menlo,monospace",
		"padding:6px 8px",
		"white-space:pre",
		"pointer-events:none"
	].join(";");
	document.body.appendChild(overlay);

	let peakOvershoot = 0;

	const render = () => {
		if (captureEvidenceOn) overlay.style.display = document.querySelector(".v-dialog.v-overlay--active") ? "" : "none";
		const container = document.querySelector(".entity-tabs .v-slide-group__container") as HTMLElement | null;
		const content = document.querySelector(".entity-tabs .v-slide-group__content") as HTMLElement | null;
		const root = document.querySelector(".entity-tabs") as HTMLElement | null;
		if (!container || !content || !root) {
			overlay.textContent = "LSP-17745: no tab strip on this view";
			return;
		}
		const tabs = Array.from(root.querySelectorAll<HTMLElement>(".v-tab"));
		const tabSum = tabs.reduce((total, tab) => total + tab.getBoundingClientRect().width, 0);
		const maxScroll = Math.max(container.scrollWidth - container.clientWidth, 0);
		const overshoot = container.scrollLeft - maxScroll;
		if (overshoot > peakOvershoot) peakOvershoot = overshoot;
		const lastTab = tabs[tabs.length - 1];
		const trailingGap = lastTab ? container.getBoundingClientRect().right - lastTab.getBoundingClientRect().right : 0;
		const viewport = window.visualViewport;

		overlay.textContent = [
			`iw=${window.innerWidth} vv=${viewport ? `${Math.round(viewport.width)}@${viewport.scale.toFixed(2)} ox=${Math.round(viewport.offsetLeft)}` : "n/a"}`,
			`cont client=${container.clientWidth} scroll=${container.scrollWidth} left=${container.scrollLeft.toFixed(1)} max=${maxScroll}`,
			`OVERSHOOT=${overshoot.toFixed(1)}  peak=${peakOvershoot.toFixed(1)}  TRAILGAP=${trailingGap.toFixed(1)}`,
			`content=${content.getBoundingClientRect().width.toFixed(1)} tabSum=${tabSum.toFixed(1)} n=${tabs.length}`,
			`affix=${!!root.querySelector(".v-slide-group__next")} cls=${root.className.replace(/v-tabs--density-default|flex-shrink-0|entity-tabs/g, "").trim()}`
		].join("\n");
	};

	let frame = 0;
	const loop = () => {
		render();
		frame = requestAnimationFrame(loop);
	};
	loop();

	return () => {
		cancelAnimationFrame(frame);
		overlay.remove();
	};
}
